// lib
import { useState, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'

import { renderProps } from '@aytm/app/stats/page/sidebar/target/converter'

// components
import TargetPanelSurvey from '@aytm/app/stats/page/sidebar/target/components/TargetPanelSurvey'
import TargetListSurvey from '@aytm/app/stats/page/sidebar/target/components/TargetListSurvey'

// styles
import style from '@aytm/app/stats/page/sidebar/target/styles/TargetPanel.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const TargetPanel = ({ survey, model }) => {
  const componentRef = useRef(null)

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  // Depend only on the fields renderProps reads; model.totals is reassigned on each counter
  // tick while the model ref stays put upstream, so tracking it (not the whole model) is what
  // re-derives the Order details row.
  const modelProps = useMemo(
    () => renderProps(survey, model),
    [
      survey.class,
      survey.locale,
      survey.traits,
      survey.target_market,
      survey.legalCompliance,
      model.totals,
      model.isExternalSurvey
    ]
  )

  return (
    <div ref={setComponentRef} className={style.component}>
      <div className={style['component-title']}>Study Details</div>

      <div className={style.row}>
        <span className={`${style.column} ${style['column-label']}`}>Ordered:</span>
        <div className={`${style.column} ${style['column-values']}`}>{PageGlobals.SURVEY_STARTED_AT || 'N/A'}</div>
      </div>
      <div className={style.row}>
        <span className={`${style.column} ${style['column-label']}`}>Completed:</span>
        <div className={`${style.column} ${style['column-values']}`}>
          {PageGlobals.SURVEY_FINISHED_AT ||
            (PageGlobals.SURVEY_STARTED_AT && 'in progress') ||
            (PageGlobals.HAS_FAKE_RESPONSES && 'artificial responses') ||
            'N/A'}
        </div>
      </div>
      <div className={style.row}>
        <span className={`${style.column} ${style['column-label']}`}>Responses:</span>
        <div className={`${style.column} ${style['column-values']}`}>
          {modelProps.totalRespondents}/{modelProps.respondents} <span>fulfilled</span>
        </div>
      </div>
      {modelProps.video_respondents ? (
        <div className={style.row}>
          <span className={`${style.column} ${style['column-label']}`}>Video clips:</span>
          <div className={`${style.column} ${style['column-values']}`}>
            {modelProps.totalVideoResponses}/{modelProps.video_respondents} <span>fulfilled</span>
          </div>
        </div>
      ) : null}
      <div className={style.row}>
        <span className={`${style.column} ${style['column-label']}`}>Type:</span>
        <div className={`${style.column} ${style['column-values']}`}>{modelProps.research_type || 'None'}</div>
      </div>

      <div className={style.section}>
        <div className={style.row}>
          <div className="p-fluid p-col-9">
            <a
              className="p-component p-button p-button-rounded p-button-blue p-button-secondary"
              href={PageGlobals.SURVEY_PREVIEW_PATH}
              target="_blank"
              rel="noreferrer"
            >
              <i className="icon-aytmfd-preview-v2" />
              <span className={style['button-text']}>Survey preview</span>
            </a>
          </div>
        </div>
        {modelProps.legalCompliance.map(item => (
          <div key={item.locale} className={style.row}>
            <div className="p-fluid p-col-9">
              <a
                className="p-component p-button p-button-rounded p-button-blue p-button-secondary"
                href={item.link}
                target="_blank"
                rel="noreferrer"
              >
                <i className="icon-aytmfd-pdf-v2" />
                <span className={style['button-text']}>Legal document ({item.locale})</span>
              </a>
            </div>
          </div>
        ))}
      </div>

      {0 < modelProps.tags.filter(tag => tag.name && 0 < tag.name?.length).length ? (
        <div className={style.section}>
          <div className={style['section-title']}>Prequals</div>

          {modelProps.tags
            .filter(tag => tag.name && 0 < tag.name?.length)
            .map(tag => (
              <div key={tag.name} className={style.row}>
                <div className={style.column}>
                  {tag.custom ? (
                    'Custom question'
                  ) : (
                    <>
                      {tag.name} {tag.positive ? 'only' : 'excluded'}
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      ) : null}

      {model.isExternalSurvey ? (
        <TargetListSurvey model={modelProps} />
      ) : (
        <TargetPanelSurvey root={root} model={modelProps} />
      )}
    </div>
  )
}

TargetPanel.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object
}

TargetPanel.displayName = 'TargetPanel'

export default TargetPanel
